import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Play, Image as ImageIcon, Youtube } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { usePageTitle } from '../hooks/usePageTitle';
import { usePageContent } from '../hooks/usePageContent';
import { useLanguage, useT } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABELS = {
  general: 'General',
  satsang: 'Satsang',
  mahayaga: 'Mahayaga',
  mantra_japa: 'Mantra Japa',
  event: 'Event',
};

const youtubeEmbed = (url) => {
  if (!url) return null;
  const patterns = [
    /youtube\.com\/embed\/([\w-]{11})/,
    /youtu\.be\/([\w-]{11})/,
    /youtube\.com\/watch\?v=([\w-]{11})/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return `https://www.youtube.com/embed/${m[1]}`;
  }
  return url;
};

export const Gallery = () => {
  usePageTitle('Gallery & Resources');
  const { t } = usePageContent('gallery');
  const tr = useT();
  const { stateScope, region } = useLanguage();
  const [media, setMedia] = useState([]);
  const [resources, setResources] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePreview, setActivePreview] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('all');

  useEffect(() => {
    fetchAll();
  }, [stateScope]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [g, r, v] = await Promise.all([
        axios.get(`${API_URL}/api/gallery`, { params: { state: stateScope } }),
        axios.get(`${API_URL}/api/resources`),
        axios.get(`${API_URL}/api/videos`),
      ]);
      setMedia(g.data);
      setResources(r.data);
      setVideos(v.data);
    } catch (error) {
      toast.error('Failed to load gallery');
    } finally {
      setLoading(false);
    }
  };

  const filteredMedia = categoryFilter === 'all' ? media : media.filter((m) => m.category === categoryFilter);
  const categories = Array.from(new Set(media.map((m) => m.category || 'general')));

  return (
    <div className="min-h-screen pt-20" data-testid="gallery-page">
      <section className="py-24 md:py-32 bg-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <div className="text-saffron-600 uppercase text-xs md:text-sm tracking-[0.2em] font-body font-semibold mb-4">
              {t('hero_overline', 'Spiritual Resources')}
            </div>
            <h1 className="text-4xl md:text-6xl font-heading font-bold text-charcoal-800 tracking-tighter leading-none mb-6">
              {t('hero_title', 'Gallery & Resources')}
            </h1>
            <p className="text-lg md:text-xl text-charcoal-600 font-body leading-relaxed max-w-3xl mx-auto">
              {t('hero_description', 'Explore our collection of spiritual articles, sacred quotes, videos, and inspirational content.')}
            </p>
            {region && (
              <p className="text-sm text-saffron-700 font-body mt-4">
                {tr('Showing gallery from')} <strong>{region.display}</strong>
              </p>
            )}
          </motion.div>

          {loading ? (
            <div className="text-center py-12 text-charcoal-500">{tr('Loading...')}</div>
          ) : (
            <>
              {media.length > 0 && (
                <div className="mb-20">
                  <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
                    <h2 className="text-2xl md:text-3xl font-heading text-charcoal-800">
                      {tr('Photos & Videos')}
                    </h2>
                    {categories.length > 1 && (
                      <div className="flex flex-wrap gap-2" data-testid="gallery-category-filters">
                        <button
                          onClick={() => setCategoryFilter('all')}
                          className={`px-3 py-1.5 rounded-full text-xs font-body font-semibold transition-colors ${
                            categoryFilter === 'all' ? 'bg-saffron-600 text-white' : 'bg-white text-charcoal-600 border border-charcoal-200 hover:border-saffron-400'
                          }`}
                          data-testid="gallery-filter-all"
                        >
                          {tr('All')}
                        </button>
                        {categories.map((c) => (
                          <button
                            key={c}
                            onClick={() => setCategoryFilter(c)}
                            className={`px-3 py-1.5 rounded-full text-xs font-body font-semibold transition-colors ${
                              categoryFilter === c ? 'bg-saffron-600 text-white' : 'bg-white text-charcoal-600 border border-charcoal-200 hover:border-saffron-400'
                            }`}
                            data-testid={`gallery-filter-${c}`}
                          >
                            {tr(CATEGORY_LABELS[c] || c)}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {filteredMedia.map((m, i) => {
                      const isYT = m.media_type === 'youtube';
                      const preview = isYT ? youtubeEmbed(m.media_url) : m.media_url;
                      return (
                        <motion.div
                          key={m.id}
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.5, delay: (i % 6) * 0.05 }}
                          viewport={{ once: true }}
                          className="bg-white rounded-xl shadow-[0_8px_30px_rgba(44,30,22,0.06)] border border-charcoal-100 overflow-hidden"
                          data-testid={`gallery-media-${m.id}`}
                        >
                          <button
                            onClick={() => setActivePreview({ url: preview, isPhoto: m.media_type === 'photo' })}
                            className="relative w-full h-48 group block"
                          >
                            <img
                              src={m.thumbnail_url || m.media_url}
                              alt={m.title}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                            {isYT && (
                              <div className="absolute inset-0 bg-black/30 group-hover:bg-black/50 transition-colors flex items-center justify-center">
                                <div className="w-16 h-16 bg-saffron-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                                  <Play className="text-white ml-1" size={28} fill="white" />
                                </div>
                              </div>
                            )}
                          </button>
                          <div className="p-5">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="bg-saffron-50 text-saffron-600 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider inline-flex items-center gap-1">
                                {isYT ? <Youtube size={11} /> : <ImageIcon size={11} />}
                                {tr(CATEGORY_LABELS[m.category] || m.category || 'General')}
                              </div>
                            </div>
                            <h3 className="text-lg font-heading text-charcoal-800">{tr(m.title)}</h3>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              )}

              {videos.length > 0 && (
                <div className="mb-20">
                  <h2 className="text-2xl md:text-3xl font-heading text-charcoal-800 mb-8">
                    {t('videos_heading', 'Watch & Learn')}
                  </h2>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {videos.map((v, i) => (
                      <motion.div
                        key={v.id}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: i * 0.1 }}
                        viewport={{ once: true }}
                        className="bg-white rounded-xl shadow-[0_8px_30px_rgba(44,30,22,0.06)] border border-charcoal-100 overflow-hidden"
                        data-testid={`video-card-${i}`}
                      >
                        <button
                          onClick={() => setActivePreview({ url: v.video_url, isPhoto: false })}
                          className="relative w-full h-48 group"
                        >
                          <img
                            src={v.thumbnail_url || 'https://images.unsplash.com/photo-1519414442781-fbd745c5b497?q=85&w=800'}
                            alt={v.title}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/40 group-hover:bg-black/60 transition-colors flex items-center justify-center">
                            <div className="w-16 h-16 bg-saffron-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                              <Play className="text-white ml-1" size={28} fill="white" />
                            </div>
                          </div>
                        </button>
                        <div className="p-6">
                          <div className="bg-saffron-50 text-saffron-600 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider inline-block mb-3">
                            {tr(v.category)}
                          </div>
                          <h3 className="text-lg font-heading text-charcoal-800 mb-2">{tr(v.title)}</h3>
                          <p className="text-sm text-charcoal-600 line-clamp-2">{tr(v.description)}</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}

              {resources.length > 0 && (
                <div>
                  <h2 className="text-2xl md:text-3xl font-heading text-charcoal-800 mb-8">
                    {t('articles_heading', 'Sacred Articles & Wisdom')}
                  </h2>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {resources.map((r, i) => (
                      <motion.div
                        key={r.id}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: i * 0.1 }}
                        viewport={{ once: true }}
                        className="bg-white rounded-xl shadow-[0_8px_30px_rgba(44,30,22,0.06)] border border-charcoal-100 overflow-hidden"
                        data-testid={`resource-card-${i}`}
                      >
                        {r.image_url && <img src={r.image_url} alt={r.title} className="w-full h-48 object-cover" />}
                        <div className="p-6">
                          <div className="bg-gold-100 text-gold-600 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider inline-block mb-3">
                            {tr(r.category)}
                          </div>
                          <h3 className="text-xl md:text-2xl font-heading leading-snug text-charcoal-800 mb-3">{tr(r.title)}</h3>
                          <p className="text-sm text-charcoal-600 leading-relaxed">{tr(r.content)}</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}

              {media.length === 0 && videos.length === 0 && resources.length === 0 && (
                <p className="text-center text-lg text-charcoal-600 py-12">{tr('No resources available at the moment.')}</p>
              )}
            </>
          )}
        </div>
      </section>

      {activePreview && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setActivePreview(null)}>
          <div className="relative w-full max-w-4xl aspect-video" onClick={(e) => e.stopPropagation()}>
            {activePreview.isPhoto ? (
              <img src={activePreview.url} alt="Gallery preview" className="w-full h-full object-contain rounded-xl" />
            ) : (
              <iframe
                src={activePreview.url}
                title="Video Preview"
                className="w-full h-full rounded-xl"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};
